import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
 Eye, 
 CheckCircle, 
 XCircle, 
 User,
 Download,
 Search,
 ListChecks,
} from 'lucide-react';
import { Button } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import { useVendorBookings } from '../../services/vendorBookingService';
import DatePicker from '../../components/client/DatePicker';
import CustomSelect from '../../components/shared/CustomSelect';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const VendorBookingHistory = () => {
 const navigate = useNavigate();
 const [filters, setFilters] = useState({
 status: '',
 client_search: '',
 service_search: '',
 date_from: '',
 date_to: ''
 });

 // Clean filters to remove empty values
 const cleanFilters = Object.fromEntries(
 Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
 );

 // Fetch all bookings for history (completed, cancelled, no_show)
 const { data: bookingsResponse, isLoading, error } = useVendorBookings({
 ...cleanFilters,
 status: ['completed', 'cancelled', 'no_show'].join(',') // Show historical bookings
 });

 const bookings = bookingsResponse?.data?.data || [];

 // Get status configuration
 const getStatusConfig = (status) => {
 const statusConfig = {
 completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
 cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelled' },
 no_show: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'No Show' }
 };
 return statusConfig[status] || statusConfig.completed;
 };

 // Define table columns
 const columns = [
 {
 key: 'booking_reference',
 label: 'Booking #',
 sortable: true,
 width: 'w-40',
 render: (booking) => (
 <span className="font-mono text-sm font-medium text-ink">
 #{booking.booking_reference}
 </span>
 )
 },
 {
 key: 'client',
 label: 'Client',
 sortable: true,
 width: 'w-56',
 render: (booking) => (
 <div className="flex items-center space-x-3 min-w-0">
 <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
 <User className="w-4 h-4 text-gray-600" />
 </div>
 <div className="min-w-0">
 <p className="text-sm font-medium text-gray-900 truncate">
 {booking.client?.name || 'Unknown Client'}
 </p>
 <p className="text-xs text-gray-500 truncate">
 {booking.client?.phone || 'No phone'}
 </p>
 </div>
 </div>
 )
 },
 {
 key: 'service',
 label: 'Service',
 sortable: true,
 width: 'w-48',
 render: (booking) => (
 <div className="min-w-0">
 <p className="text-sm font-medium text-gray-900 truncate">
 {booking.vendor_service?.service_name || 'Unknown Service'}
 </p>
 <p className="text-xs text-gray-500 truncate">
 {booking.vendor_service?.service_category?.category_name || 'No category'}
 </p>
 </div>
 )
 },
 {
 key: 'event_date',
 label: 'Event Date',
 sortable: true,
 width: 'w-36',
 render: (booking) => (
 <div className="flex-shrink-0 min-w-0">
 <p className="text-sm text-gray-900">
 {new Date(booking.event_date).toLocaleDateString()}
 </p>
 <p className="text-xs text-gray-500">
 {booking.start_time ? new Date(booking.start_time).toLocaleTimeString([], { 
 hour: '2-digit', 
 minute: '2-digit' 
 }) : 'TBD'}
 </p>
 </div>
 )
 },
 {
 key: 'total_amount',
 label: 'Amount',
 sortable: true,
 width: 'w-32',
 render: (booking) => (
 <div className="text-right">
 <p className="text-sm font-medium text-gray-900">
 ${Number(booking.total_amount || 0).toFixed(2)}
 </p>
 <p className="text-xs text-gray-500">
 {booking.full_payment_paid ? 'Paid' : 'Unpaid'}
 </p>
 </div>
 )
 },
 {
 key: 'status',
 label: 'Status',
 sortable: true,
 width: 'w-32',
 render: (booking) => {
 const statusConfig = getStatusConfig(booking.status);
 const StatusIcon = statusConfig.icon;
 return (
 <span className="whitespace-nowrap flex-shrink-0">
 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
 <StatusIcon className="w-3 h-3 mr-1" />
 {statusConfig.label}
 </span>
 </span>
 );
 }
 },
 {
 key: 'updated_at',
 label: 'Completed Date',
 sortable: true,
 width: 'w-36',
 render: (booking) => (
 <div className="flex-shrink-0 min-w-0">
 <p className="text-sm text-gray-900">
 {new Date(booking.updated_at).toLocaleDateString()}
 </p>
 <p className="text-xs text-gray-500">
 {new Date(booking.updated_at).toLocaleTimeString([], { 
 hour: '2-digit', 
 minute: '2-digit' 
 })}
 </p>
 </div>
 )
 }
 ];

 // Define table actions
 const actions = [
 {
 label: 'View Details',
 icon: Eye,
 variant: 'default',
 onClick: (booking) => {
 navigate(`/vendor/bookings/${booking.id}`);
 }
 }
 ];

 // Handle search
 const handleSearch = (data, searchTerm) => {
 if (!searchTerm) return data;
 
 return data.filter(booking => 
 booking.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 booking.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 booking.vendor_service?.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 booking.event_location?.toLowerCase().includes(searchTerm.toLowerCase())
 );
 };

 // Handle filter changes
 const handleFilterChange = (key, value) => {
 setFilters(prev => ({
 ...prev,
 [key]: value
 }));
 };

 // Clear all filters
 const clearAllFilters = () => {
 setFilters({
 status: '',
 client_search: '',
 service_search: '',
 date_from: '',
 date_to: ''
 });
 };

 if (isLoading) {
 return (
 <div className="min-h-screen bg-canvas flex items-center justify-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ink"></div>
 </div>
 );
 }

 if (error) {
 return (
 <div className="min-h-screen bg-canvas flex items-center justify-center">
 <div className="text-center">
 <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading History</h3>
 <p className="text-gray-600">Unable to load booking history.</p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Booking History"
 subtitle="View completed and cancelled bookings."
 actions={
 <Button variant="outline" icon={Download}>
 Export
 </Button>
 }
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.bookingHistory.title}
 steps={VENDOR_PAGE_GUIDES.bookingHistory.steps}
 icon={ListChecks}
 />

 {/* Filters */}
 <div className="product-panel p-4 sm:p-6">
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
 <div>
 <CustomSelect
 label="Status"
 value={filters.status}
 onChange={(value) => handleFilterChange('status', value)}
 options={[
 { value: '', label: 'All Statuses' },
 { value: 'completed', label: 'Completed' },
 { value: 'cancelled', label: 'Cancelled' },
 { value: 'no_show', label: 'No Show' }
 ]}
 size="sm"
 className="w-full"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-ink-secondary mb-2">
 Client Search
 </label>
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
 <input
 type="text"
 value={filters.client_search}
 onChange={(e) => handleFilterChange('client_search', e.target.value)}
 placeholder="Search by client name..."
 className="w-full pl-10 pr-3 py-2 border border-border bg-surface text-ink rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ink/40 focus:border-ink"
 />
 </div>
 </div>
 
 <div>
 <label className="block text-sm font-medium text-ink-secondary mb-2">
 Service Search
 </label>
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
 <input
 type="text"
 value={filters.service_search}
 onChange={(e) => handleFilterChange('service_search', e.target.value)}
 placeholder="Search by service..."
 className="w-full pl-10 pr-3 py-2 border border-border bg-surface text-ink rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ink/40 focus:border-ink"
 />
 </div>
 </div>

 <div>
 <DatePicker
 label="From Date"
 value={filters.date_from}
 onChange={(value) => handleFilterChange('date_from', value)}
 placeholder="Select start date"
 size="sm"
 className="w-full"
 />
 </div>

 <div>
 <DatePicker
 label="To Date"
 value={filters.date_to}
 onChange={(value) => handleFilterChange('date_to', value)}
 placeholder="Select end date"
 minDate={filters.date_from || undefined}
 size="sm"
 className="w-full"
 />
 </div>
 </div>
 
 <div className="flex justify-end mt-4">
 <Button
 variant="outline"
 onClick={clearAllFilters}
 className="rounded-md"
 >
 Clear Filters
 </Button>
 </div>
 </div>

 {/* Data Table */}
 <div className="product-panel overflow-hidden">
 <DataTable
 data={bookings}
 columns={columns}
 actions={actions}
 onSearch={handleSearch}
 searchPlaceholder="Search booking history..."
 emptyMessage="No booking history found"
 loading={isLoading}
 />
 </div>
 </div>
 );
};

export default VendorBookingHistory;
